import { useEffect, useState, useCallback } from "react";
import { FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { ATT_STATUS, fmtTime, fmtDate, fmtDur } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Reports() {
  const [rows, setRows] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/attendance").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (from && r.date < from) return false;
    if (to && r.date > to) return false;
    return true;
  });

  const download = async (format) => {
    setBusy(format);
    try {
      const { data } = await api.get("/reports/attendance/export", {
        params: { format, date_from: from || undefined, date_to: to || undefined },
        responseType: "blob",
      });
      const ext = format === "pdf" ? "pdf" : "xlsx";
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan-absensi-showty-${new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Laporan diekspor (${ext.toUpperCase()})`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight lg:text-3xl">Laporan Absensi</h1>
          <p className="mt-1 text-sm text-slate-500">Rekap kehadiran seluruh karyawan. Ekspor dalam format Excel atau PDF.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="report-from-date" />
          <span className="text-slate-400">—</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="report-to-date" />
          <Button data-testid="export-excel-button" disabled={busy === "excel"} onClick={() => download("excel")} className="gap-2">
            <FileSpreadsheet className="h-4 w-4" /> {busy === "excel" ? "..." : "Excel"}
          </Button>
          <Button data-testid="export-pdf-button" disabled={busy === "pdf"} onClick={() => download("pdf")} variant="outline" className="gap-2">
            <FileText className="h-4 w-4" /> {busy === "pdf" ? "..." : "PDF"}
          </Button>
        </div>
      </div>

      <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-slate-800">
          <h3 className="font-heading text-lg font-semibold">Rekap ({filtered.length} baris)</h3>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tanggal</TableHead><TableHead>Karyawan</TableHead><TableHead>Departemen</TableHead>
                <TableHead>Masuk</TableHead><TableHead>Keluar</TableHead><TableHead>Durasi</TableHead>
                <TableHead>Terlambat</TableHead><TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} className="py-8 text-center text-slate-400">Tidak ada data.</TableCell></TableRow>
              )}
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid="report-row">
                  <TableCell>{fmtDate(r.date)}</TableCell>
                  <TableCell><p className="font-medium">{r.full_name}</p><p className="font-mono text-xs text-slate-400">{r.employee_code}</p></TableCell>
                  <TableCell className="text-sm">{r.department_name}</TableCell>
                  <TableCell className="font-mono text-sm">{fmtTime(r.clock_in_time)}</TableCell>
                  <TableCell className="font-mono text-sm">{fmtTime(r.clock_out_time)}</TableCell>
                  <TableCell className="font-mono text-sm">{fmtDur(r.work_duration)}</TableCell>
                  <TableCell className="font-mono text-sm">{r.late_minutes || 0}m</TableCell>
                  <TableCell><span className={`rounded-full border px-2 py-0.5 text-xs ${ATT_STATUS[r.attendance_status]?.cls}`}>{ATT_STATUS[r.attendance_status]?.label || r.attendance_status}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
